import { Chess, swapColor } from "@chesshotel/chess";
import { detectHangingPieces, exchangeEvaluation, getThreats, pieceValues, uci2move, } from "./util.js";
import { ChessTermType, TermAction, } from "@chesshotel/types";
import { detectTrappedPiece } from "./trappedPiece.js";
export function detectLeaveUndefended2(fen, move) {
    const chess = new Chess(fen);
    if (chess.inCheck()) {
        return null;
    }
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const threats = getThreats(fen, opponentColor);
    const moveResult = chess.move(uci2move(move));
    if (chess.inCheck()) {
        return null;
    }
    const threatsAfter = getThreats(chess.fen(), opponentColor);
    const newThreats = threatsAfter.filter((threat) => {
        return !threats.find((t) => t.from.square == threat.from.square && t.to.square == threat.to.square);
    });
    let gain = 0;
    if (moveResult.captured) {
        gain = pieceValues[moveResult.captured];
    }
    if (newThreats.length > 0) {
        const newThreat = newThreats.sort((a, b) => {
            return a.gain - b.gain;
        })[0];
        gain += newThreat.gain;
        const mainPiece = newThreat.to;
        return {
            term: ChessTermType.UNDEFENDED_SACRIFICE,
            move: moveResult,
            mainPiece,
            termAction: TermAction.PERFORM,
        };
    }
    return null;
}
export function detectLeaveUndefended(fen, move) {
    const chess = new Chess(fen);
    if (chess.inCheck()) {
        return null;
    }
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const threats = getThreats(fen, opponentColor);
    const moveResult = chess.move(uci2move(move));
    const hangingPiecesAfter = detectHangingPieces(chess.fen(), opponentColor).sort((a, b) => {
        return pieceValues[a.type] - pieceValues[b.type];
    });
    if (chess.inCheck()) {
        return null;
    }
    const threatsAfter = getThreats(chess.fen(), opponentColor);
    const newThreats = threatsAfter.filter((threat) => {
        return !threats.find((t) => t.from.square == threat.from.square && t.to.square == threat.to.square);
    });
    if (hangingPiecesAfter.length > 0) {
        const hangingPiece = hangingPiecesAfter.sort((a, b) => {
            return pieceValues[a.type] - pieceValues[b.type];
        })[0];
        const hangingPieceGain = pieceValues[hangingPiece.type];
        let gain = 0;
        if (moveResult.captured) {
            gain = pieceValues[moveResult.captured];
        }
        if (newThreats.length > 0) {
            const newThreatGain = newThreats.sort((a, b) => {
                return a.gain - b.gain;
            })[0].gain;
            gain += newThreatGain;
        }
        if (hangingPieceGain - gain < 2) {
            return null;
        }
        return {
            term: ChessTermType.UNDEFENDED_SACRIFICE,
            move: moveResult,
            mainPiece: hangingPiece,
            translationKeys: { piece: hangingPiece.type },
            termAction: TermAction.PERFORM,
        };
    }
    return null;
}
export function detectIgnoreThreat(fen, move, threats) {
    const chess = new Chess(fen);
    const color = chess.turn();
    if (threats == null) {
        return null;
    }
    const moveResult = chess.move(uci2move(move));
    const threatsAfter = getThreats(chess.fen(), color);
    if (threatsAfter.length == threats.length && threats.length == 1) {
        const sameThreat = threats.find((threat) => {
            return threatsAfter.find((t) => t.from.square == threat.from.square &&
                t.to.square == threat.to.square &&
                t.gain > 1);
        });
        if (sameThreat) {
            return {
                translationKeys: { piece: sameThreat.to.type },
                arrows: [{ from: sameThreat.from.square, to: sameThreat.to.square }],
                term: ChessTermType.IGNORE_THREAT,
                termAction: TermAction.PERFORM,
                move: moveResult,
            };
        }
    }
}
export function detectPositiveSacrifice(position, lastPosition) {
    const sacrifice = detectSacrifice(lastPosition.fen, position.move);
    if (sacrifice) {
        return {
            ...sacrifice,
            termAction: TermAction.PERFORM,
        };
    }
}
export function detectPositiveIgnoreThreat(position, lastPosition) {
    const ignoreThreat = detectLeaveUndefended(lastPosition.fen, position.move);
    if (ignoreThreat) {
        return {
            ...ignoreThreat,
            termAction: TermAction.PERFORM,
        };
    }
}
function getNewThreats(threatsBefore, threatsAfter) {
    const newThreats = threatsAfter.filter((threat) => {
        return !threatsBefore.find((t) => t.from.square == threat.from.square && t.to.square == threat.to.square);
    });
    return newThreats;
}
export function detectSacrifice(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const moveObj = uci2move(moveStr);
    const threatsBefore = getThreats(chess.fen(), opponentColor);
    const evalResult = exchangeEvaluation(chess.fen(), moveObj.to, {
        from: moveObj.from,
        backtrackBadMoves: true,
        backtrackKeepFirstMove: true,
        filteredByLoss: false,
    });
    const hasPromotion = evalResult.moveSequence.find((move) => {
        return move.promotion;
    });
    if (hasPromotion) {
        return null;
    }
    if (evalResult.gain <= -1 &&
        evalResult.moveSequence.length >= 2 &&
        evalResult.pieceWin != "p") {
        const trappedPiece = detectTrappedPiece(fen, moveObj.from);
        if (trappedPiece) {
            return null;
        }
        const moveResult = chess.move(moveObj);
        const nextMove = evalResult.moveSequence[1];
        chess.move(nextMove);
        const threatsAfter = getThreats(chess.fen(), opponentColor);
        const newThreats = getNewThreats(threatsBefore, threatsAfter);
        const newThreatBetterGain = newThreats.find((threat) => {
            return threat.gain >= Math.abs(evalResult.gain);
        });
        if (newThreatBetterGain) {
            return null;
        }
        return {
            term: ChessTermType.SACRIFICE,
            mainPiece: chess.getBoardPiece(moveResult.to),
            move: moveResult,
            evalResult,
        };
    }
}
//# sourceMappingURL=sacrifice.js.map